import React, { CSSProperties, FC, useEffect, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from "apexcharts";
import IView from "../../types/view";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import ViewsFilter, { months } from "./ViewsFilter";
import { setOptionsAction } from "../../store/reducers/commonSlice";
import 'react-tooltip/dist/react-tooltip.css';
import { Tooltip } from 'react-tooltip';
import { renderToString } from "react-dom/server";
import useGraph from "../../hooks/useGraph";

const BarChart = () => {
    const {
        isMonthly,
        mount,
        totalCount,
        totalDaysCount,
        averagePerDay,
        data,
        averagePerPeriod,
        totalAbnormalDays,
        totalPerPeriodLabel,
        abnormalDaysLabel,
        totalPeriodCount,
        isError,
        isLoading
    } = useGraph()

    const dispatch = useAppDispatch()
    const searchPage = useAppSelector(state => state.search.page)
    const values: IView[] = useAppSelector(state => state.search.views)

    const customTooltipHandler = (options: any) => {
        return renderToString(<div className="apexcharts-tooltip-text py-[3px] px-2 text-xs">
            <div className="apexcharts-tooltip-z-group py-1">
                <span className="apexcharts-tooltip-text-z-label">Date: </span>
                <span className="apexcharts-tooltip-text-z-value">{data.labels[options.dataPointIndex]}</span>
            </div>
            <div className="apexcharts-tooltip-z-group py-[3px]">
                <span className="apexcharts-tooltip-text-z-label">Views: </span>
                <span className="apexcharts-tooltip-text-z-value">{options.series[0][options.dataPointIndex]}</span>
            </div>
            {data.abnormalDays[options.dataPointIndex] > 0 && <div className="apexcharts-tooltip-z-group py-[3px]">
                {isMonthly ? <>
                    <span className="apexcharts-tooltip-text-z-label">Abnormal days: </span>
                    <span className="apexcharts-tooltip-text-z-value">{data.abnormalDays[options.dataPointIndex]}</span>
                </> : <>
                    {data.abnormalDays[options.dataPointIndex] > 0 && <span className="apexcharts-tooltip-text-z-label flex items-center">
                        <span className="w-[10px] h-[10px] bg-red-500 rounded-[50%] block mr-1" />
                        <span>Abnormal day</span>
                    </span>}
                </>}
            </div>}
        </div>)
    }

    const state = {
        series: [{
            name: 'Views',
            data: data.count
        }],
        options: {
            legend: {
                show: false
            },
            chart: {
                height: 300,
                toolbar: {
                    show: false
                },
                events: {
                    click: (event, chartContext, config) => {
                        if (!isMonthly) {
                            return;
                        }
                        if (!data.labels[config.dataPointIndex]) {
                            return;
                        }
                        if (!searchPage) {
                            return;
                        }

                        const dateString = data.dates[config.dataPointIndex]
                        const dateArray = dateString.split('/')

                        dispatch(setOptionsAction({
                            revisionsFilterValue: +dateArray[2],
                            revisionsFilterPeriod: 'year',
                            revisionsFilterMonthValue: +dateArray[1],
                        }))
                    }
                }
            },
            dataLabels: {
                enabled: true,
                style: {
                    fontSize: '8px',
                    fontWeight: 'normal',
                    colors: ['white']
                }
            },
            plotOptions: {
                bar: {
                    barHeight: '100%',
                    distributed: true,
                    horizontal: false,
                }
            },
            grid: {
                show: false,
                margin: {
                    top: 0,
                    bottom: 0,
                },
                padding: {
                    top: 0,
                    bottom: 0,
                },
            },
            colors: data.colors,
            states: {
                active: {
                    filter: {
                        type: 'none'
                    }
                }
            },
            stroke: {
                width: 1,
                colors: ['#fff'],
            },
            xaxis: {
                categories: data.labels,
                tickAmount: mount,
                labels: {
                    rotateAlways: true,
                    rotate: -45,
                    style: {
                        // fontWeight: 'bold'
                    }
                }
            },
            yaxis: {
                labels: {
                    show: false
                }
            },
            tooltip: {
                enabled: true,
                custom: customTooltipHandler
            }
        } as ApexOptions,
    };

    if (!searchPage) {
        return <></>;
    }

    return (
        <section className="py-[40px]" style={{ background: 'rgba(0, 0, 0, 0.04)' }}>
            <div className="container">
                <div className="flex items-start mb-[40px] justify-center md:justify-between flex-col md:flex-row">
                    <h2 className="text-lg font-bold text-gray-900 md:text-2xl lg:text-3xl inline-flex mb-3 md:mb-0 w-full md:w-auto justify-center md:justify-start">
                        <mark className="px-2 py-1 text-white bg-[#546E7A] rounded">Page view stats</mark>
                    </h2>
                    <ViewsFilter />
                </div>
                {(!data.labels.length && !isLoading) ? <div className="flex items-center justify-center w-full min-h-[400px]">
                    <h3 className="text-[30px] text-danger">View statistics are not available</h3>
                </div> : <>
                    <div className="min-h-[400px]">
                        <Chart
                            options={state.options}
                            series={state.series}
                            type="bar"
                            height={380}
                        />
                    </div>
                    <div className="mt-3">
                        <span className="mr-4 bg-gray-200 px-3 py-2 rounded-lg inline-block">
                            <b>Total views for {totalPerPeriodLabel}: </b>{totalPeriodCount}
                        </span>
                        {isMonthly ? <>
                            <span className="mr-4 bg-gray-200 px-3 py-2 rounded-lg inline-block">
                                <b>Average views per month: </b>{averagePerPeriod}
                            </span>
                            <span className="mr-4 bg-gray-200 px-3 py-2 rounded-lg inline-block">
                                <b>Average views per day: </b>{averagePerDay}
                            </span>
                            {/*<span className="mr-4 bg-gray-200 px-3 py-2 rounded-lg inline-block">*/}
                            {/*    <b>Average views per week: </b>{Math.round(averagePerPeriod / 4.2857)}*/}
                            {/*</span>*/}
                        </> : <>
                            <span className="mr-4 bg-gray-200 px-3 py-2 rounded-lg inline-block">
                                <b>Average views per day: </b>{averagePerDay}
                            </span>
                        </>}
                    </div>
                    <div className="mt-3">
                        <a data-tooltip-id="abnormal-days-tooltip">
                            <span className="mr-4 bg-gray-200 px-3 py-2 rounded-lg inline-block">
                                <b>Number of abnormal days ({abnormalDaysLabel}): </b>{totalAbnormalDays}
                            </span>
                        </a>
                        <Tooltip id="abnormal-days-tooltip" place="top" style={{ maxWidth: 300, padding: '16px 25px' }}>
                            An abnormal day is defined as any day during any given period when the number of views exceeds the daily average by threefold or more. Click on 'Graph Analysis' if you notice any days highlighted in red on your graph.
                        </Tooltip>
                    </div>
                </>}
            </div>
        </section>
    );
};

export default BarChart;